import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { mapKey, subjectFromObs } from 'core/rx-js';
import { Observable, Subject, combineLatest, distinctUntilChanged, filter, map, of, startWith, takeUntil } from 'rxjs';
import { DsgDeepTranslationKey } from '../../pipes/translate.pipe';
import { DataGridService, } from './data-grid.service';
import { PaginatorComponent } from './paginator/paginator.component';


export type ColumnType = 'number' | 'string' | 'date' | 'select' | 'actions'
export interface IColumn<TModel extends any = any> {
  title?: boolean,
  dataField: string,
  caption: DsgDeepTranslationKey,
  showInCollapsed?: boolean,
  collapseTo?: string,
  icon?: string | ((data: TModel) => string),
  type: ColumnType,
  template?: string,
  collapsedColor?: ThemePalette | ((data: TModel) => ThemePalette),
  calculateDisplay?: (data: TModel) => string | Observable<string>,
  sticky?: boolean,
  stickyEnd?: boolean
}

@Component({
  selector: 'ss-data-grid',
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.scss'],
  providers: [
    DataGridService
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataGridComponent<T extends any = any> {
  readonly #destroy = new Subject<void>()

  @Input() gridTitle: DsgDeepTranslationKey

  readonly getRowLoaderKeysSubject = this._dataGridService.getRowLoaderKeysSubject
  @Input() get getRowLoaderKeys() {
    return this.getRowLoaderKeysSubject.value
  }
  set getRowLoaderKeys(value) {
    this.getRowLoaderKeysSubject.next(value)
  }

  readonly columnsSubject = this._dataGridService.columnsSubject;
  @Input() set columns(value: IColumn[]) {
    this.columnsSubject.next(value)
  }
  get columns() {
    return this.columnsSubject.value
  }

  readonly loadFnSubject = this._dataGridService.loadFnSubject;
  get loadFn() {
    return this.loadFnSubject.value;
  }
  @Input() set loadFn(value) {
    this.loadFnSubject.next(value)
  }

  readonly keyFieldSubject = this._dataGridService.keyFieldSubject
  @Input() get keyField() {
    return this.keyFieldSubject.value
  }
  set keyField(value) {
    this.keyFieldSubject.next(value)
  }

  readonly customizeRowSubject = this._dataGridService.customizeRowSubject;
  @Input() get customizeRow() {
    return this.customizeRowSubject.value
  }
  set customizeRow(value) {
    this.customizeRowSubject.next(value)
  }

  readonly updateDataFnSubject = this._dataGridService.updateDataFnSubject
  @Input() get updateDataFn() {
    return this.updateDataFnSubject.value
  }
  set updateDataFn(value) {
    this.updateDataFnSubject.next(value)
  }

  readonly showSearchSubject = this._dataGridService.showSearchSubject;
  @Input() get showSearch() {
    return this.showSearchSubject.value
  }
  set showSearch(value) {
    this.showSearchSubject.next(value)
  }


  @ViewChildren('scroller', { read: ElementRef })
  private _scrollerQL: QueryList<ElementRef<HTMLElement>>


  readonly columnsObs = this._dataGridService.columnsObs;
  readonly displayedColumnsObs = this.columnsObs.pipe(
    map(columns => columns.map(({ dataField }) => dataField))
  )

  readonly actionsTemplateSubject = this._dataGridService.actionsTemplateSubject;
  @Input() get actionsTemplate() {
    return this.actionsTemplateSubject.value
  }
  set actionsTemplate(value) {
    this.actionsTemplateSubject.next(value)
  }

  readonly cellTemplatesSubject = this._dataGridService.cellTemplatesSubject;
  @Input() get cellTemplates() {
    return this.cellTemplatesSubject.value
  }
  set cellTemplates(value) {
    this.cellTemplatesSubject.next(value)
  }

  readonly mobileItemTemplateSubject = this._dataGridService.mobileItemTemplateSubject;
  @Input() get mobileItemTemplate() {
    return this.mobileItemTemplateSubject.value
  }
  set mobileItemTemplate(value) {
    this.mobileItemTemplateSubject.next(value)
  }

  readonly toolbarTemplateSubject = this._dataGridService.toolbarTemplateSubject
  @Input() get toolbarTemplate() {
    return this.toolbarTemplateSubject.value
  }
  set toolbarTemplate(value) {
    this.toolbarTemplateSubject.next(value)
  }


  readonly displayedDataSubject = this._dataGridService.displayedDataSubject;
  readonly searchSubject = this._dataGridService.searchSubject;
  readonly showCollapsedModeSubject = this._dataGridService.showCollapsedModeSubject;
  readonly showSearchObs = this._dataGridService.showSearchObs;


  readonly titleColumnObs = this.columnsSubject.pipe(
    filter(Boolean),
    map(columns => columns.find((column) => column.title))
  )

  readonly #titleColumnsSubject = subjectFromObs({
    initialValue: null,
    observable: this.titleColumnObs,
    until: this.#destroy
  })

  get titleColumn() {
    return this.#titleColumnsSubject.value
  }

  readonly titleDataFieldObs = this.titleColumnObs.pipe(
    filter(Boolean),
    mapKey('dataField'),
    distinctUntilChanged()
  )

  readonly showCollapsedColumnsObs = this.columnsObs.pipe(
    filter(Boolean),
    map((columns) => columns.some(col => col.type !== 'actions' && !col.title))
  )

  @ViewChild('cardHeader', { read: ElementRef })
  private _header: ElementRef<HTMLElement>
  get header() {
    return this._header?.nativeElement
  }
  @ViewChild(PaginatorComponent, { read: ElementRef })
  private _paginator: ElementRef<HTMLElement>
  get paginator() {
    return this._paginator?.nativeElement
  }
  @Output()
  readonly rowClick = new EventEmitter<T>()
  get baseElement() {
    return this._elementRef.nativeElement
  }


  readonly showNoDataObs = combineLatest([this._dataGridService.totalLengthSubject, this._dataGridService.searchSubject]).pipe(
    map(([totalLength, search]) => !search && totalLength === 0),
    distinctUntilChanged(),
  )

  readonly showHeaderObs = this._dataGridService.showHeaderObs

  constructor(
    private readonly _dataGridService: DataGridService,
    private readonly _elementRef: ElementRef<HTMLElement>
  ) { }

  emitRowClicked(row: T, column: IColumn) {
    if (column.type === 'actions') {
      return;
    }
    this.rowClick.emit(row);
  }

  ngAfterViewInit() {




    this._scrollerQL.changes.pipe(
      startWith(this._scrollerQL),
      map((scrollerQL: QueryList<ElementRef<HTMLElement>>) => scrollerQL.first),
      distinctUntilChanged(),
      takeUntil(this.#destroy)

    )
      .subscribe((scrollerRef) => {
        this._dataGridService.scrollerSubject.next(scrollerRef?.nativeElement)
      })

  }


  getColumn(dataField: string) {

    return this.columns?.find((col) => col.dataField === dataField);
  }

  getCellDisplay(rowData: any, dataField: string): any {

    const value = rowData[dataField];
    const column = this.getColumn(dataField);
    if (!column) {
      return value;
    }

    if (column.type === 'date') {

      return of(formatDate(value, 'dd/MM/yyyy', 'en-us'));
    }

    if (!column.calculateDisplay) {
      return of(value);
    }
    const display = column?.calculateDisplay(rowData);

    if (!(display instanceof Observable)) {
      return of(display);
    }

    return display;
  }


  customizeElement(element: HTMLElement, rowData: any) {
    if (!this.customizeRow) {
      return;
    }
    return this.customizeRow?.(element, rowData);
  }

  public refresh() {

    this._dataGridService.refresh();
  }

  getColor(col: IColumn, rowData: any) {

    if (!col.collapsedColor) {
      return;
    }

    return typeof col.collapsedColor === 'function' ? col.collapsedColor(rowData) : col.collapsedColor

  }

  getCollapsedColorClass(col: IColumn, rowData: any) {
    const color = this.getColor(col, rowData);
    if (!color) {
      return '';
    }

    return color + '-text'
  }

  getIcon(col: IColumn, rowData: any) {


    if (!col.icon) {
      return;
    }

    return typeof col.icon === 'function' ? col.icon(rowData) : col.icon
  }


  ngOnDestroy() {
    this.#destroy.next()
    this.#destroy.complete()
  }
}




